//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Search Select component
//
//  Copyright:    © 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.6.0
//    001   15.07.25 Sean Flook       IMANN-1864 Initial Revision.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    002   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    003   28.08.25 Sean Flook       IMANN-1812 Updated renderOption in Autocomplete.
//    004   04.11.25 Sean Flook       IMANN-2120 Use the correct type when clearing variables.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";

import UserContext from "../context/userContext";

import { lookupToTitleCase } from "../utils/HelperUtils";

import { GetStreetSearchURL, GetStreetUsrnSearchURL } from "../configuration/ADSConfig";

import { Grid, TextField, Typography, Tooltip, Autocomplete, Skeleton } from "@mui/material";
import { Box, Stack } from "@mui/system";
import ADSErrorDisplay from "./ADSErrorDisplay";

import { adsDarkGrey } from "../utils/ADSColours";
import {
  FormBoxRowStyle,
  FormRowStyle,
  FormSelectInputStyle,
  FormInputStyle,
  controlLabelStyle,
  skeletonHeight,
  tooltipStyle,
} from "../utils/ADSStyles";

ADSSelectStreetControl.propTypes = {
  label: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  isRequired: PropTypes.bool,
  isFocused: PropTypes.bool,
  doNotSetTitleCase: PropTypes.bool,
  loading: PropTypes.bool,
  displayNoChange: PropTypes.bool,
  language: PropTypes.string,
  helperText: PropTypes.string,
  value: PropTypes.any,
  errorText: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

function ADSSelectStreetControl({
  label,
  isEditable = false,
  isRequired = false,
  isFocused = false,
  doNotSetTitleCase = false,
  loading = false,
  displayNoChange = false,
  language = "ENG",
  helperText,
  value,
  errorText,
  onChange,
}) {
  const userContext = React.useContext(UserContext);

  const [lookupData, setLookupData] = useState([]);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState();
  const currentSearch = useRef(null);

  const [displayError, setDisplayError] = useState("");
  const hasError = useRef(false);
  const [inputValue, setInputValue] = useState("");
  const currentValue = useRef(null);

  /**
   * Event to handle when the control value changes.
   *
   * @param {object} event The event object.
   * @param {string} newValue The updated value
   */
  const handleInputChangeEvent = (event, newValue) => {
    if (newValue && newValue.length > 0) {
      setSearch(newValue);
      setInputValue(lookupToTitleCase(newValue, doNotSetTitleCase) || "");
    }
  };

  /**
   * Event to handle when the control value changes.
   *
   * @param {object} event The event object.
   * @param {string} newValue The updated value
   */
  const handleChangeEvent = (event, newValue) => {
    if (onChange) {
      const selectedItem = getSelectedItem(newValue);
      if (selectedItem) onChange(selectedItem.usrn);
      else onChange(null);
    }
  };

  /**
   * Method to get the selected item.
   *
   * @param {string} value The display value for the control
   * @returns {array|null} The lookup row with the given label.
   */
  function getSelectedItem(value) {
    if (!lookupData || !value) return null;
    return lookupData.find((x) => x.displayText === value);
  }

  useEffect(() => {
    const streetUsrnSearchUrl = GetStreetUsrnSearchURL(userContext.currentUser);

    const apiFetch = async (url, headers) => {
      try {
        const response = await fetch(url, {
          headers: headers,
          crossDomain: true,
          method: "GET",
        });

        switch (response.status) {
          case 200:
            const data = await response.json();
            return data;

          case 204:
            return [];

          case 401:
            userContext.onExpired();
            return [];

          default:
            return [];
        }
      } catch (err) {
        return [];
      }
    };

    if ((!inputValue || currentValue.current !== value) && value && value !== 0 && streetUsrnSearchUrl) {
      currentValue.current = value;

      const url = `${streetUsrnSearchUrl.url}?usrn=${value}`;
      apiFetch(url, streetUsrnSearchUrl.headers).then((res) => {
        setLookupData(res);
        const mappedData = res.filter((x) => x.language === language).map((x) => x.displayText);
        setOptions(mappedData);
        setInputValue(lookupToTitleCase(mappedData[0], doNotSetTitleCase) || "");
      });
    } else if (!value && value !== 0 && !!currentValue.current) {
      setInputValue("");
    }
  }, [inputValue, value, userContext, doNotSetTitleCase, language]);

  useEffect(() => {
    hasError.current = errorText && errorText.length > 0;

    if (hasError.current) {
      if (Array.isArray(errorText)) setDisplayError(errorText.join(", "));
      else setDisplayError(errorText);
    } else setDisplayError(null);
  }, [errorText]);

  useEffect(() => {
    if (isFocused) {
      let element = null;

      element = document.getElementById(`ads-select-${label.toLowerCase().replaceAll(" ", "-")}`);

      if (element) element.focus();
    }
  }, [isFocused, label]);

  useEffect(() => {
    const blankRecord = {
      id: 0,
      language: "ENG",
      street: "",
      locality: "",
      town: "",
      displayText: "",
      usrn: 0,
      street_ref_type: 0,
      properties_in_usrn: 0,
    };

    const streetSearchUrl = GetStreetSearchURL(userContext.currentUser);

    const apiFetch = async (url, headers, blankData, dataIfAborted, signal) => {
      try {
        const response = await fetch(url, {
          headers: headers,
          crossDomain: true,
          method: "GET",
          signal,
        });

        switch (response.status) {
          case 200:
            const data = await response.json();
            return data;

          case 204:
            return [{ ...blankData, displayText: "No records found" }];

          case 401:
            userContext.onExpired();
            return [{ ...blankData, displayText: "Search failed..." }];

          default:
            return [{ ...blankData, displayText: "Search failed..." }];
        }
      } catch (err) {
        if (err.name === "AbortError") {
          return dataIfAborted;
        }
        return err;
      }
    };

    const controller = new window.AbortController();

    if (search && search.length > 0 && search !== currentSearch.current) {
      currentSearch.current = search;
      if (streetSearchUrl) {
        setOptions(["Loading Data..."]);
        const whileWaiting = [{ ...blankRecord, displayText: "Loading Data..." }];

        const signal = controller.signal;

        const url = `${streetSearchUrl.url}?street=${search}`;
        apiFetch(url, streetSearchUrl.headers, blankRecord, whileWaiting, signal).then((res) => {
          setLookupData(res);
          const mappedData = res.filter((x) => x.language === language).map((x) => x.displayText);
          setOptions(mappedData);
        });
      } else {
        setLookupData([]);
        setOptions([]);
      }
    }

    return () => {
      setOptions(["Loading Data..."]);
      controller.abort();
    };
  }, [language, search, userContext]);

  return (
    <Box sx={FormBoxRowStyle(hasError.current)}>
      <Grid container justifyContent="flex-start" alignItems="center" sx={FormRowStyle(hasError.current)}>
        <Grid size={3}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mr: "16px" }}>
            <Typography
              id={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
              variant="body2"
              align="left"
              sx={controlLabelStyle}
            >
              {`${label}${isRequired ? "*" : ""}`}
            </Typography>
          </Stack>
        </Grid>
        <Grid size={9}>
          {loading ? (
            <Skeleton variant="rectangular" animation="wave" height={`${skeletonHeight}px`} width="100%" />
          ) : isEditable ? (
            helperText && helperText.length > 0 ? (
              <Tooltip
                title={isRequired ? helperText + " This is a required field." : helperText}
                arrow
                placement="right"
                sx={tooltipStyle}
              >
                <Autocomplete
                  id={`ads-select-${label.toLowerCase().replaceAll(" ", "-")}`}
                  sx={{
                    color: "inherit",
                  }}
                  getOptionLabel={(option) => lookupToTitleCase(option, doNotSetTitleCase)}
                  noOptionsText={`No ${label} records`}
                  options={options}
                  autoHighlight
                  autoSelect
                  onChange={handleChangeEvent}
                  inputValue={inputValue}
                  onInputChange={handleInputChangeEvent}
                  renderOption={(props, option, state, ownerState) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={key} component="li" {...optionProps}>
                        <Typography variant="body2" sx={{ color: adsDarkGrey }} align="left">
                          {lookupToTitleCase(option, doNotSetTitleCase)}
                        </Typography>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={FormSelectInputStyle(hasError.current)}
                      error={hasError.current}
                      fullWidth
                      disabled={!isEditable}
                      required={isRequired}
                      placeholder={displayNoChange ? "No change" : null}
                      variant="outlined"
                      margin="dense"
                      size="small"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                        },
                      }}
                    />
                  )}
                  aria-labelledby={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
                />
              </Tooltip>
            ) : (
              <Autocomplete
                id={`ads-select-${label.toLowerCase().replaceAll(" ", "-")}`}
                sx={{
                  color: "inherit",
                }}
                getOptionLabel={(option) => lookupToTitleCase(option, doNotSetTitleCase)}
                noOptionsText={`No ${label} records`}
                options={options}
                autoHighlight
                autoSelect
                onChange={handleChangeEvent}
                inputValue={inputValue}
                onInputChange={handleInputChangeEvent}
                renderOption={(props, option, state, ownerState) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box key={key} component="li" {...optionProps}>
                      <Typography variant="body2" sx={{ color: adsDarkGrey }} align="left">
                        {lookupToTitleCase(option, doNotSetTitleCase)}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={FormSelectInputStyle(hasError.current)}
                    error={hasError.current}
                    fullWidth
                    disabled={!isEditable}
                    required={isRequired}
                    placeholder={displayNoChange ? "No change" : null}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                      },
                    }}
                  />
                )}
                aria-labelledby={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
              />
            )
          ) : (
            <TextField
              id={`${label.toLowerCase().replaceAll(" ", "-")}-lookup-info`}
              sx={FormInputStyle(hasError.current)}
              error={hasError.current}
              rows={1}
              fullWidth
              disabled
              required={isRequired}
              variant="outlined"
              margin="dense"
              size="small"
              value={lookupToTitleCase(inputValue, doNotSetTitleCase)}
            />
          )}
        </Grid>
        <ADSErrorDisplay errorText={displayError} id={`${label.toLowerCase().replaceAll(" ", "-")}-select-error`} />
      </Grid>
    </Box>
  );
}

export default ADSSelectStreetControl;
